import React from "react";
import NextHead from "next/head";
import { useRouter } from "next/router";
import useTranslation from "next-translate/useTranslation";

import { getIndexableLocales, getUrlWithLocale, IUrlArgs } from "./utils";

interface IProps {
  pageNamespace: string;
  ogImage: string;
  noindex?: boolean;
}

export const Head: React.FC<IProps> = ({ pageNamespace, ogImage, noindex }) => {
  const { t } = useTranslation();
  const { locale, locales, defaultLocale, pathname } = useRouter();

  const pageTitle = t(`${pageNamespace}:meta.title`);
  const pageDescription = t(
    `${pageNamespace}:meta.description`,
    {},
    { fallback: "common:meta.title" },
  );

  // TODO: move to dotenv?
  const pageHostname = "https://magicul.io";

  const urlArgs: IUrlArgs = {
    base: pageHostname,
    path: pathname,
    locale: locale!,
    defaultLocale: defaultLocale!,
    locales: locales!,
  };

  const isConverterPage = pathname.startsWith("/converter/");

  /**
   * Untranslated locales serve the English fallback verbatim, so they point
   * their canonical at the English URL instead of at themselves, and stay out
   * of the hreflang cluster — otherwise every page ships up to five identical
   * English URLs with identical titles.
   */
  const indexableLocales = getIndexableLocales(pathname, defaultLocale!);
  const canonicalLocale = indexableLocales.includes(locale!)
    ? locale!
    : defaultLocale!;

  // og:url and the schema URLs follow the canonical, not the served locale.
  const pageUrl = getUrlWithLocale({ ...urlArgs, locale: canonicalLocale });

  return (
    <NextHead>
      {/* General */}
      <title>{pageTitle}</title>
      <meta name="author" content="Magicul" />
      <meta name="publisher" content="Magicul" />
      <meta name="copyright" content="Magicul" />
      <meta name="description" content={pageDescription} />
      <meta
        name="keywords"
        content={t(
          `${pageNamespace}:meta.keywords`,
          {},
          { fallback: "common:meta.title" },
        )}
      />
      <meta name="page-type" content="Software" />

      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Links */}
      {indexableLocales.map(alternateLocale => (
        <link
          key={alternateLocale}
          rel="alternate"
          hrefLang={alternateLocale}
          href={getUrlWithLocale({ ...urlArgs, locale: alternateLocale })}
        />
      ))}
      <link
        rel="alternate"
        hrefLang="x-default"
        href={getUrlWithLocale({ ...urlArgs, locale: defaultLocale! })}
      />
      <link
        rel="canonical"
        href={getUrlWithLocale({ ...urlArgs, locale: canonicalLocale })}
      />

      {/* OG */}
      <meta property="og:site_name" content="Magicul" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={`${pageHostname}${ogImage}`} />
      <meta property="og:image:alt" content={pageTitle} />

      {/* Structured data — tells Google the site name is "Magicul" */}
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Magicul",
            alternateName: "Magicul.io",
            url: pageHostname,
          }),
        }}
      />
      {isConverterPage && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: JSON.stringify([
              {
                "@context": "https://schema.org",
                "@type": "WebApplication",
                name: pageTitle,
                url: pageUrl,
                description: pageDescription,
                applicationCategory: "DesignApplication",
                operatingSystem: "Web",
                offers: {
                  "@type": "Offer",
                  price: "94",
                  priceCurrency: "USD",
                },
              },
              {
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: [
                  {
                    "@type": "ListItem",
                    position: 1,
                    name: "Magicul",
                    item: pageHostname,
                  },
                  {
                    "@type": "ListItem",
                    position: 2,
                    name: pageTitle,
                    item: pageUrl,
                  },
                ],
              },
            ]),
          }}
        />
      )}
    </NextHead>
  );
};
